import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Copy, RefreshCw, Loader2, Lightbulb, X, Download, ClipboardCopy } from 'lucide-react';
import { useClipTitles } from '@/hooks/use-clip-titles';
import { toast } from '@/hooks/use-toast';

interface ClipTitlesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipName: string;
  existingCopy?: any;
}

export const ClipTitlesDialog: React.FC<ClipTitlesDialogProps> = ({
  open,
  onOpenChange,
  clipId,
  clipName,
  existingCopy,
}) => {
  const { t } = useTranslation();
  const {
    titlesData,
    isGenerating,
    isPolling,
    error,
    generateTitles,
  } = useClipTitles(clipId, existingCopy);

  const isLoading = isGenerating || isPolling;
  const hasTitles = titlesData && (titlesData.title_list?.length || titlesData.thumb_list?.length);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: t('clipTitles.toast.copied'),
        description: t('clipTitles.toast.copiedToClipboard'),
      });
    });
  };

  const handleGenerate = () => {
    generateTitles();
  };

  const buildFullText = () => {
    if (!titlesData) return '';
    const lines: string[] = [];
    const titles = [...(titlesData.title_list || []), ...(titlesData.thumb_list || [])];
    if (titles.length > 0) {
      lines.push(`Título: ${titles[0]}`);
    }
    if (titles.length > 0 && titlesData.description) {
      lines.push('');
    }
    if (titlesData.description) {
      lines.push(`Descrição: ${titlesData.description}`);
    }
    if (titlesData.tags) {
      lines.push(`Tags: ${titlesData.tags}`);
    }
    if (titles.length > 1) {
      lines.push('');
      lines.push('Títulos alternativos:');
      titles.slice(1).forEach((t) => lines.push(`- ${t}`));
    }
    return lines.join('\n');
  };

  const handleCopyAll = () => {
    const text = buildFullText();
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: t('clipTitles.toast.copied'),
        description: t('clipTitles.toast.copiedToClipboard'),
      });
    });
  };

  const handleDownloadTxt = () => {
    const text = buildFullText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clipName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={() => onOpenChange(false)}
    >
      <Card
        className="w-full max-w-2xl my-8 shadow-lg border"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              {t('clipTitles.titlesAndSuggestions')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('clipTitles.titlesGeneratedBy')} <strong>{clipName}</strong>
          </p>
        </CardHeader>

        <CardContent className="space-y-2">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  {isGenerating ? t('clipTitles.startingGeneration') : t('clipTitles.generatingTitles')}
                </p>
              </div>
            </div>
          )}

          {hasTitles && !isLoading && (
            <div className="space-y-2">
              {/* Title List */}
              {titlesData.title_list && titlesData.title_list.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium mb-3">{t('clipTitles.mainTitles')}</h3>
                  <div className="space-y-2">
                    {[...titlesData.title_list, ...titlesData.thumb_list].map((title, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-3 py-1 rounded-lg border"
                      >
                        <span className="text-sm flex-1">{title}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(title)}
                          className="ml-2"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {titlesData.description && (
                <div>
                  <h3 className="text-sm font-medium mb-3">{t('clipTitles.description')}</h3>
                  <div className="p-3 rounded-lg border">
                    <p className="text-sm text-white-700 mb-2">{titlesData.description}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(titlesData.description!)}
                      className="mt-2"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {t('clipTitles.copyDescription')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Tags */}
              {titlesData.tags && (
                <div>
                  <h3 className="text-sm font-medium mb-3">{t('clipTitles.tags')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {titlesData.tags.split(',').map((tag, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="cursor-pointer hover:bg-gray-100 hover:text-black"
                        onClick={() => copyToClipboard(tag.trim())}
                      >
                        {tag.trim()}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(titlesData.tags!)}
                    className="mt-2"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    {t('clipTitles.copyAllTags')}
                  </Button>
                </div>
              )}

              <Separator />
            </div>
          )}

          {!hasTitles && !isLoading && !error && (
            <div className="text-center py-8">
              <Lightbulb className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-muted-foreground mb-4">
                {t('clipTitles.noTitlesGenerated')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('clipTitles.clickToGenerate')}
              </p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('clipTitles.close')}
          </Button>
          <div className="flex gap-2">
            {hasTitles && (
              <>
                <Button
                  variant="outline"
                  onClick={handleCopyAll}
                  className="gap-2"
                >
                  <ClipboardCopy className="w-4 h-4" />
                  {t('clipTitles.copyAll')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadTxt}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('clipTitles.downloadTxt')}
                </Button>
              </>
            )}
            <Button
              onClick={handleGenerate}
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {hasTitles ? t('clipTitles.regenerate') : t('clipTitles.generateTitles')}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ClipTitlesDialog;
